import { Injectable } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { NavigationUtilitiesService } from "@common/route/navigation-utilities.service";
import { UserService } from "@common/user/user.service";
import { AuthorisationNotificationService } from "@org-common/lib/authorisation/authorisation-notification.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { BaseOrgCommonNavigationHierarchyService } from "@org-common/lib/sidebar/base-org-common-navigation-hierarchy.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { teamDashboardPageRoute } from "../team-dashboard/team-dashboard-page/team-dashboard-page.route";
import { TeamHierarchyNavigationService } from "./team-hierarchy-navigation.service";

@Injectable()
export class OwnerHierarchyNavigationService extends BaseOrgCommonNavigationHierarchyService {
    public static readonly HierarchyId = "ADAPT.Owners.Teams.Navigation.Hierarchy";
    public static readonly IconClass = "fal fa-user-key";
    public id = OwnerHierarchyNavigationService.HierarchyId;

    public constructor(
        private navUtilitiesFactory: NavigationUtilitiesService,
        private teamsService: CommonTeamsService,
        private userService: UserService,
        private teamHierarchyNavService: TeamHierarchyNavigationService,
        orgService: OrganisationService,
        authNotification: AuthorisationNotificationService,
    ) {
        super(orgService, authNotification);
    }

    protected async buildHierarchy() {
        // only a single owner team - cannot create more than 1 - so no point creating hierarchy with multiple teams
        // if for some reason there are multiple owner teams, we will just show the first one
        let ownerTeams: Team[] = [];
        if (this.userService.currentPerson?.isStakeholderManager || this.userService.currentPerson?.isCoach()) {
            ownerTeams = await this.teamsService.promiseToGetActiveOwnerTeams();
        } else {
            ownerTeams = await this.teamsService.promiseToGetActiveOwnersTeamsForCurrentPerson();
        }

        if (ownerTeams.length === 0) {
            // should not happen - as the display$ observable will stop the tab from being displayed if there is no accessible owner team
            // - just return an empty node
            return this.navUtilitiesFactory.nodeBuilder().build();
        }

        return this.promiseToBuildTeamNavigationNode(ownerTeams[0]);
    }

    @Autobind
    private async promiseToBuildTeamNavigationNode(team: Team) {
        const builder = this.navUtilitiesFactory.nodeBuilderForControllerAndParams(teamDashboardPageRoute.id, { teamId: team.teamId })
            .setTitle(team.name)
            .setIconClass(OwnerHierarchyNavigationService.IconClass)
            .keepChildrenInAddedOrder()
            .setCustomKeyValue("expandOnLoad", true);

        return this.teamHierarchyNavService.promiseToBuildTeamNavigationNode(team, builder);
    }
}
